import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, Upload, Mail, Target, Award, 
  Activity, Plus, Search, Filter, Download, Send,
  CheckCircle, Clock, XCircle, BarChart3, UserPlus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const NetworkGrowth = () => {
  const [activeSection, setActiveSection] = useState('invitations');
  const [growthStats, setGrowthStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGrowthStats();
  }, []);

  const fetchGrowthStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/growth/dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGrowthStats(response.data);
    } catch (error) {
      console.error('Error fetching growth stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Growth Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Invitations</CardTitle>
            <Mail className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              {growthStats?.invitations?.total || 0}
            </div>
            <div className="flex items-center space-x-2 mt-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-600">
                {growthStats?.invitations?.acceptance_rate || 0}% acceptance rate
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Waitlist</CardTitle>
            <Clock className="w-5 h-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              {growthStats?.waitlist?.waiting || 0}
            </div>
            <p className="text-sm text-gray-600 mt-2">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Target Lists</CardTitle>
            <Target className="w-5 h-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              {growthStats?.target_lists?.total_targets || 0}
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Across {growthStats?.target_lists?.lists || 0} lists
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">VIP Onboarding</CardTitle>
            <Award className="w-5 h-5 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              {growthStats?.vip_onboarding?.active || 0}
            </div>
            <p className="text-sm text-gray-600 mt-2">Active VIP plans</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Sections */}
      <Tabs value={activeSection} onValueChange={setActiveSection}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="invitations">
            <Mail className="w-4 h-4 mr-2" />
            Invitations
          </TabsTrigger>
          <TabsTrigger value="waitlist">
            <Clock className="w-4 h-4 mr-2" />
            Waitlist
          </TabsTrigger>
          <TabsTrigger value="targets">
            <Target className="w-4 h-4 mr-2" />
            Target Lists
          </TabsTrigger>
          <TabsTrigger value="vip">
            <Award className="w-4 h-4 mr-2" />
            VIP
          </TabsTrigger>
          <TabsTrigger value="intelligence">
            <Activity className="w-4 h-4 mr-2" />
            Intel
          </TabsTrigger>
        </TabsList>

        {/* Invitations Section */}
        <TabsContent value="invitations">
          <InvitationsSection />
        </TabsContent>

        {/* Waitlist Section */}
        <TabsContent value="waitlist">
          <WaitlistSection />
        </TabsContent>

        {/* Target Lists Section */}
        <TabsContent value="targets">
          <TargetListsSection />
        </TabsContent>

        {/* VIP Onboarding Section */}
        <TabsContent value="vip">
          <VIPOnboardingSection />
        </TabsContent>

        {/* Competitive Intelligence Section */}
        <TabsContent value="intelligence">
          <CompetitiveIntelligenceSection />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ==================== INVITATIONS SECTION ====================
const InvitationsSection = () => {
  const [invitations, setInvitations] = useState([]);
  const [batches, setBatches] = useState([]);
  const [invitationStats, setInvitationStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const [showUploadCSV, setShowUploadCSV] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const [batchForm, setBatchForm] = useState({
    name: '',
    campaign_type: 'onboarding',
    message_template: ''
  });

  useEffect(() => {
    fetchInvitationData();
  }, [filterStatus]);

  const fetchInvitationData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch stats
      const statsRes = await axios.get(`${API_BASE_URL}/api/admin/growth/invitations/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitationStats(statsRes.data);
      setBatches(statsRes.data.batches || []);

      // Fetch invitations
      const invitesRes = await axios.get(
        `${API_BASE_URL}/api/admin/growth/invitations${filterStatus !== 'all' ? `?status=${filterStatus}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvitations(invitesRes.data.invitations || []);
    } catch (error) {
      console.error('Error fetching invitation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBatch = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/growth/invitations/batch`,
        batchForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowCreateBatch(false);
      setBatchForm({ name: '', campaign_type: 'onboarding', message_template: '' });
      fetchInvitationData();
    } catch (error) {
      console.error('Error creating batch:', error);
      alert('Failed to create batch');
    }
  };

  const handleUploadCSV = async (e) => {
    e.preventDefault();
    if (!selectedBatch) {
      alert('Please select a batch first');
      return;
    }

    const formData = new FormData();
    const fileInput = document.getElementById('csv-file');
    formData.append('file', fileInput.files[0]);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/growth/invitations/upload-csv?batch_id=${selectedBatch}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      setShowUploadCSV(false);
      fetchInvitationData();
      alert('CSV uploaded successfully!');
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert('Failed to upload CSV');
    }
  };

  const handleSendReminder = async (invitationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/growth/invitations/${invitationId}/send-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Reminder sent!');
      fetchInvitationData();
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Failed to send reminder');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{invitationStats?.total_invitations || 0}</div>
            <p className="text-sm text-gray-600">Total Invitations</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-orange-600">{invitationStats?.pending || 0}</div>
            <p className="text-sm text-gray-600">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{invitationStats?.accepted || 0}</div>
            <p className="text-sm text-gray-600">Accepted</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">{invitationStats?.acceptance_rate || 0}%</div>
            <p className="text-sm text-gray-600">Acceptance Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="ignored">Ignored</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Dialog open={showCreateBatch} onOpenChange={setShowCreateBatch}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Batch
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Invitation Batch</DialogTitle>
                <DialogDescription>
                  Create a new batch to organize your invitations
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateBatch} className="space-y-4">
                <div>
                  <Label htmlFor="batch-name">Batch Name</Label>
                  <Input
                    id="batch-name"
                    placeholder="e.g., Q1 2025 Trainers"
                    value={batchForm.name}
                    onChange={(e) => setBatchForm({...batchForm, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="campaign-type">Campaign Type</Label>
                  <Select 
                    value={batchForm.campaign_type}
                    onValueChange={(value) => setBatchForm({...batchForm, campaign_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="onboarding">Onboarding</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message-template">Message Template</Label>
                  <Textarea
                    id="message-template"
                    placeholder="Enter your invitation message..."
                    rows={4}
                    value={batchForm.message_template}
                    onChange={(e) => setBatchForm({...batchForm, message_template: e.target.value})}
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreateBatch(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Create Batch</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showUploadCSV} onOpenChange={setShowUploadCSV}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Invitation CSV</DialogTitle>
                <DialogDescription>
                  Upload a CSV file with columns: email, name, user_type, company, linkedin_url, notes
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUploadCSV} className="space-y-4">
                <div>
                  <Label htmlFor="select-batch">Select Batch</Label>
                  <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a batch" />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.map((batch) => (
                        <SelectItem key={batch.id} value={batch.id}>
                          {batch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="csv-file">CSV File</Label>
                  <Input
                    id="csv-file"
                    type="file"
                    accept=".csv"
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowUploadCSV(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Upload</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Invitations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Invitations List</CardTitle>
        </CardHeader>
        <CardContent>
          {invitations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No invitations found. Create a batch and upload CSV to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium">Name</th>
                    <th className="text-left p-3 font-medium">Email</th>
                    <th className="text-left p-3 font-medium">Type</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Reminders</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((invite) => (
                    <tr key={invite.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">{invite.name}</td>
                      <td className="p-3">{invite.email}</td>
                      <td className="p-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {invite.user_type}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          invite.status === 'accepted' ? 'bg-green-100 text-green-800' :
                          invite.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {invite.status}
                        </span>
                      </td>
                      <td className="p-3">{invite.reminder_count || 0}</td>
                      <td className="p-3">
                        {invite.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendReminder(invite.id)}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            Remind
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== WAITLIST SECTION ====================
const WaitlistSection = () => {
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({
    email: '',
    name: '',
    reason: '',
    priority: 'normal'
  });

  useEffect(() => {
    fetchWaitlist();
  }, []);

  const fetchWaitlist = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/growth/waitlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaitlist(response.data.waitlist || []);
    } catch (error) {
      console.error('Error fetching waitlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/growth/waitlist`,
        entryForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowAddEntry(false);
      setEntryForm({ email: '', name: '', reason: '', priority: 'normal' });
      fetchWaitlist();
    } catch (error) {
      console.error('Error adding entry:', error);
      alert('Failed to add entry');
    }
  };

  const handleApprove = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/admin/growth/waitlist/${entryId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Entry approved and invited!');
      fetchWaitlist();
    } catch (error) {
      console.error('Error approving entry:', error);
      alert('Failed to approve entry');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Waitlist Management</h3>
        <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add to Waitlist
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add to Waitlist</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddEntry} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={entryForm.name}
                  onChange={(e) => setEntryForm({...entryForm, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={entryForm.email}
                  onChange={(e) => setEntryForm({...entryForm, email: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="reason">Reason</Label>
                <Textarea
                  id="reason"
                  value={entryForm.reason}
                  onChange={(e) => setEntryForm({...entryForm, reason: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select 
                  value={entryForm.priority}
                  onValueChange={(value) => setEntryForm({...entryForm, priority: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAddEntry(false)}>
                  Cancel
                </Button>
                <Button type="submit">Add to Waitlist</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="pt-6">
          {waitlist.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No waitlist entries yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium">Name</th>
                    <th className="text-left p-3 font-medium">Email</th>
                    <th className="text-left p-3 font-medium">Reason</th>
                    <th className="text-left p-3 font-medium">Priority</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {waitlist.map((entry) => (
                    <tr key={entry.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">{entry.name}</td>
                      <td className="p-3">{entry.email}</td>
                      <td className="p-3">{entry.reason}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          entry.priority === 'high' ? 'bg-red-100 text-red-800' :
                          entry.priority === 'normal' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {entry.priority}
                        </span>
                      </td>
                      <td className="p-3">{entry.status}</td>
                      <td className="p-3">
                        {entry.status === 'waiting' && (
                          <Button
                            size="sm"
                            onClick={() => handleApprove(entry.id)}
                          >
                            Approve
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== TARGET LISTS SECTION ====================
const TargetListsSection = () => {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateList, setShowCreateList] = useState(false);
  const [showAddTarget, setShowAddTarget] = useState(false);

  const [listForm, setListForm] = useState({
    name: '',
    description: '',
    category: 'linkedinInfluencers'
  });

  const [targetForm, setTargetForm] = useState({
    name: '',
    email: '',
    linkedin_url: '',
    company: '',
    title: '',
    notes: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchLists();
  }, []);

  useEffect(() => {
    if (selectedList) {
      fetchTargets(selectedList);
    }
  }, [selectedList]);

  const fetchLists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/growth/target-lists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLists(response.data.lists || []);
    } catch (error) {
      console.error('Error fetching lists:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTargets = async (listId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/growth/target-lists/${listId}/targets`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTargets(response.data.targets || []);
    } catch (error) {
      console.error('Error fetching targets:', error);
    }
  };

  const handleCreateList = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/growth/target-lists`,
        listForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowCreateList(false);
      setListForm({ name: '', description: '', category: 'linkedinInfluencers' });
      fetchLists();
    } catch (error) {
      console.error('Error creating list:', error);
      alert('Failed to create list');
    }
  };

  const handleAddTarget = async (e) => {
    e.preventDefault();
    if (!selectedList) {
      alert('Please select a list first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/growth/target-lists/${selectedList}/targets`,
        targetForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowAddTarget(false);
      setTargetForm({
        name: '', email: '', linkedin_url: '', company: '',
        title: '', notes: '', priority: 'medium'
      });
      fetchTargets(selectedList);
    } catch (error) {
      console.error('Error adding target:', error);
      alert('Failed to add target');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Strategic Target Lists</h3>
        <Dialog open={showCreateList} onOpenChange={setShowCreateList}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Create List
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Target List</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateList} className="space-y-4">
              <div>
                <Label htmlFor="list-name">List Name</Label>
                <Input
                  id="list-name"
                  value={listForm.name}
                  onChange={(e) => setListForm({...listForm, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={listForm.description}
                  onChange={(e) => setListForm({...listForm, description: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={listForm.category}
                  onValueChange={(value) => setListForm({...listForm, category: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simplifyMyTraining">SimplifyMyTraining Users</SelectItem>
                    <SelectItem value="linkedinInfluencers">LinkedIn Influencers</SelectItem>
                    <SelectItem value="corporateManagers">Corporate Managers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateList(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create List</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Lists Sidebar */}
        <Card>
          <CardHeader>
            <CardTitle>Your Lists</CardTitle>
          </CardHeader>
          <CardContent>
            {lists.length === 0 ? (
              <p className="text-sm text-gray-500">No lists yet</p>
            ) : (
              <div className="space-y-2">
                {lists.map((list) => (
                  <button
                    key={list.id}
                    onClick={() => setSelectedList(list.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedList === list.id
                        ? 'bg-blue-50 border-blue-500'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-medium">{list.name}</p>
                    <p className="text-xs text-gray-600">{list.total_targets} targets</p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Targets List */}
        <Card className="md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>
              {selectedList ? 'Targets' : 'Select a list'}
            </CardTitle>
            {selectedList && (
              <Dialog open={showAddTarget} onOpenChange={setShowAddTarget}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Target
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Add Target User</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddTarget} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="target-name">Name</Label>
                        <Input
                          id="target-name"
                          value={targetForm.name}
                          onChange={(e) => setTargetForm({...targetForm, name: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="target-email">Email</Label>
                        <Input
                          id="target-email"
                          type="email"
                          value={targetForm.email}
                          onChange={(e) => setTargetForm({...targetForm, email: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="company">Company</Label>
                        <Input
                          id="company"
                          value={targetForm.company}
                          onChange={(e) => setTargetForm({...targetForm, company: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="title">Title</Label>
                        <Input
                          id="title"
                          value={targetForm.title}
                          onChange={(e) => setTargetForm({...targetForm, title: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="linkedin">LinkedIn URL</Label>
                      <Input
                        id="linkedin"
                        value={targetForm.linkedin_url}
                        onChange={(e) => setTargetForm({...targetForm, linkedin_url: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="target-notes">Notes</Label>
                      <Textarea
                        id="target-notes"
                        value={targetForm.notes}
                        onChange={(e) => setTargetForm({...targetForm, notes: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="target-priority">Priority</Label>
                      <Select 
                        value={targetForm.priority}
                        onValueChange={(value) => setTargetForm({...targetForm, priority: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setShowAddTarget(false)}>
                        Cancel
                      </Button>
                      <Button type="submit">Add Target</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </CardHeader>
          <CardContent>
            {!selectedList ? (
              <div className="text-center py-8 text-gray-500">
                Select a list to view targets
              </div>
            ) : targets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No targets in this list yet
              </div>
            ) : (
              <div className="space-y-3">
                {targets.map((target) => (
                  <div key={target.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium">{target.name}</h4>
                        {target.email && (
                          <p className="text-sm text-gray-600">{target.email}</p>
                        )}
                        {target.company && (
                          <p className="text-sm text-gray-600">{target.title} at {target.company}</p>
                        )}
                        {target.notes && (
                          <p className="text-xs text-gray-500 mt-2">{target.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          target.priority === 'high' ? 'bg-red-100 text-red-800' :
                          target.priority === 'medium' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {target.priority}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          target.status === 'joined' ? 'bg-green-100 text-green-800' :
                          target.status === 'interested' ? 'bg-blue-100 text-blue-800' :
                          target.status === 'contacted' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {target.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== VIP ONBOARDING SECTION ====================
const VIPOnboardingSection = () => {
  const [vipPlans, setVipPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateVIP, setShowCreateVIP] = useState(false);
  const [vipForm, setVipForm] = useState({
    user_email: '',
    onboarding_plan: '',
    assigned_to: '',
    priority: 'high'
  });

  useEffect(() => {
    fetchVIPPlans();
  }, []);

  const fetchVIPPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/growth/vip-onboarding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVipPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching VIP plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVIP = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/growth/vip-onboarding`,
        vipForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowCreateVIP(false);
      setVipForm({ user_email: '', onboarding_plan: '', assigned_to: '', priority: 'high' });
      fetchVIPPlans();
    } catch (error) {
      console.error('Error creating VIP plan:', error);
      alert('Failed to create VIP plan');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">VIP User Onboarding</h3>
        <Dialog open={showCreateVIP} onOpenChange={setShowCreateVIP}>
          <DialogTrigger asChild>
            <Button className="bg-yellow-600 hover:bg-yellow-700">
              <Plus className="w-4 h-4 mr-2" />
              Create VIP Plan
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create VIP Onboarding Plan</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateVIP} className="space-y-4">
              <div>
                <Label htmlFor="vip-email">User Email</Label>
                <Input
                  id="vip-email"
                  type="email"
                  value={vipForm.user_email}
                  onChange={(e) => setVipForm({...vipForm, user_email: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="assigned-to">Assigned To (Admin ID)</Label>
                <Input
                  id="assigned-to"
                  value={vipForm.assigned_to}
                  onChange={(e) => setVipForm({...vipForm, assigned_to: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="onboarding-plan">Onboarding Plan</Label>
                <Textarea
                  id="onboarding-plan"
                  rows={5}
                  placeholder="Detail the personalized onboarding steps..."
                  value={vipForm.onboarding_plan}
                  onChange={(e) => setVipForm({...vipForm, onboarding_plan: e.target.value})}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateVIP(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Plan</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="pt-6">
          {vipPlans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No VIP onboarding plans yet
            </div>
          ) : (
            <div className="space-y-4">
              {vipPlans.map((plan) => (
                <div key={plan.id} className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-medium text-lg">{plan.user_email}</h4>
                      <p className="text-sm text-gray-600">Assigned to: {plan.assigned_to}</p>
                    </div>
                    <span className={`px-3 py-1 rounded text-sm ${
                      plan.status === 'completed' ? 'bg-green-100 text-green-800' :
                      plan.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      'bg-orange-100 text-orange-800'
                    }`}>
                      {plan.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{plan.onboarding_plan}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== COMPETITIVE INTELLIGENCE SECTION ====================
const CompetitiveIntelligenceSection = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [activityForm, setActivityForm] = useState({
    platform: 'LinkedIn',
    activity_type: 'viral_post',
    description: '',
    url: '',
    impact_level: 'medium'
  });

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/growth/competitive-intelligence?days=30`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogActivity = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/growth/competitive-intelligence`,
        activityForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowLogActivity(false);
      setActivityForm({
        platform: 'LinkedIn',
        activity_type: 'viral_post',
        description: '',
        url: '',
        impact_level: 'medium'
      });
      fetchActivities();
    } catch (error) {
      console.error('Error logging activity:', error);
      alert('Failed to log activity');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Competitive Intelligence (Last 30 Days)</h3>
        <Dialog open={showLogActivity} onOpenChange={setShowLogActivity}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Log Activity
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Log Competitor Activity</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleLogActivity} className="space-y-4">
              <div>
                <Label htmlFor="platform">Platform</Label>
                <Input
                  id="platform"
                  value={activityForm.platform}
                  onChange={(e) => setActivityForm({...activityForm, platform: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="activity-type">Activity Type</Label>
                <Select 
                  value={activityForm.activity_type}
                  onValueChange={(value) => setActivityForm({...activityForm, activity_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viral_post">Viral Post</SelectItem>
                    <SelectItem value="new_feature">New Feature</SelectItem>
                    <SelectItem value="user_migration">User Migration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  rows={4}
                  value={activityForm.description}
                  onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="url">URL (Optional)</Label>
                <Input
                  id="url"
                  type="url"
                  value={activityForm.url}
                  onChange={(e) => setActivityForm({...activityForm, url: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="impact-level">Impact Level</Label>
                <Select 
                  value={activityForm.impact_level}
                  onValueChange={(value) => setActivityForm({...activityForm, impact_level: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowLogActivity(false)}>
                  Cancel
                </Button>
                <Button type="submit">Log Activity</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="pt-6">
          {activities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No competitor activities logged yet
            </div>
          ) : (
            <div className="space-y-4">
              {activities.map((activity) => (
                <div key={activity.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{activity.platform}</h4>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {activity.activity_type}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          activity.impact_level === 'high' ? 'bg-red-100 text-red-800' :
                          activity.impact_level === 'medium' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {activity.impact_level} impact
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {new Date(activity.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{activity.description}</p>
                  {activity.url && (
                    <a 
                      href={activity.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View source →
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NetworkGrowth;
